import { Link } from 'react-router-dom'
import { Cpu, RefreshCw, ArrowRight, FileArchive } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@/hooks/useAuth'
import { format } from 'date-fns'

// Mirrors the SolveJob shape stored in KV by the backend (routes/admin-solve.ts).
interface SolveJob {
  jobId: string
  filename: string
  fileSize: number
  status: 'pending' | 'uploaded' | 'queued' | 'processing' | 'completed' | 'failed'
  progress: number
  message?: string
  modelId?: string
  error?: string
  createdBy: string
  createdAt: string
  updatedAt: string
}

const STATUS_STYLES: Record<SolveJob['status'], string> = {
  pending: 'bg-gray-100 text-gray-700',
  uploaded: 'bg-blue-100 text-blue-800',
  queued: 'bg-yellow-100 text-yellow-800',
  processing: 'bg-indigo-100 text-indigo-800',
  completed: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
}

const STATUS_LABELS: Record<SolveJob['status'], string> = {
  pending: 'Pending upload',
  uploaded: 'Uploaded',
  queued: 'Queued',
  processing: 'Processing',
  completed: 'Completed',
  failed: 'Failed',
}

const isActiveJob = (s: SolveJob['status']) =>
  s === 'pending' || s === 'uploaded' || s === 'queued' || s === 'processing'

export default function SolveJobs() {
  const { token, adminEmail } = useAuth()
  const apiUrl = import.meta.env.VITE_API_URL

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['admin-solve-jobs'],
    queryFn: async () => {
      const res = await fetch(`${apiUrl}/api/v2/admin/solve`, {
        headers: { Authorization: `Bearer ${token}`, 'X-Admin-Email': adminEmail! },
      })
      if (!res.ok) throw new Error('Failed to load solve jobs')
      const body = await res.json()
      return body.data as SolveJob[]
    },
    refetchInterval: 5000, // keep in-progress jobs fresh
  })

  const jobs = data || []

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Cloud Solve Jobs</h1>
          <p className="text-sm text-gray-500 mt-1">
            In-progress solving jobs. A finished job is automatically published to the Models gallery.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => refetch()}
            className="flex items-center gap-2 px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <Link
            to="/admin/models/solve"
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <Cpu className="w-5 h-5" />
            New Solve
          </Link>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Source File
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Progress
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                    Loading solve jobs...
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-red-600">
                    Error loading solve jobs. Please try again.
                  </td>
                </tr>
              ) : jobs.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                    No solve jobs yet.
                  </td>
                </tr>
              ) : (
                jobs.map((job) => (
                  <tr key={job.jobId} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <FileArchive className="w-6 h-6 text-gray-400 mr-3 flex-shrink-0" />
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate max-w-xs">
                            {job.filename}
                          </div>
                          <div className="text-xs text-gray-500">
                            {(job.fileSize / (1024 * 1024)).toFixed(1)} MB · {job.createdBy}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ${STATUS_STYLES[job.status]}`}
                      >
                        {STATUS_LABELS[job.status]}
                      </span>
                      {job.status === 'failed' && job.error && (
                        <div className="text-xs text-red-600 mt-1 max-w-xs truncate" title={job.error}>
                          {job.error}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {isActiveJob(job.status) ? (
                        <div className="w-32">
                          <div className="flex items-center justify-between text-xs mb-1">
                            <span className="text-gray-500 truncate">{job.message || '—'}</span>
                            <span className="text-gray-700 font-medium ml-2">{job.progress}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-1.5">
                            <div
                              className="bg-indigo-600 h-1.5 rounded-full transition-all duration-300"
                              style={{ width: `${job.progress}%` }}
                            />
                          </div>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {job.createdAt ? format(new Date(job.createdAt), 'MMM d, yyyy HH:mm') : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {job.status === 'completed' && job.modelId ? (
                        <Link
                          to={`/admin/models/${job.modelId}/edit`}
                          className="text-indigo-600 hover:text-indigo-900 inline-flex items-center gap-1"
                        >
                          View Model
                          <ArrowRight size={16} />
                        </Link>
                      ) : (
                        <span className="text-gray-400 font-mono text-xs">{job.jobId}</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
